/**
 * Consumption presets for Quick G14 Calculator
 *
 * 24-hour consumption weight distributions for 3 lifestyle patterns.
 * Values are relative weights (not actual kWh) — normalized before use.
 * Based on typical Polish household patterns from TAURON data.
 */

export type ConsumptionPreset = 'day' | 'even' | 'night';

export interface PresetConfig {
  label: string;
  description: string;
  icon: 'sun' | 'clock' | 'moon';
  /** 24-element array: relative consumption weight per hour (0-23) */
  hourlyWeights: number[];
}

/**
 * Hourly consumption weights per lifestyle pattern.
 * Index 0 = midnight (00:00), index 23 = 11 PM (23:00).
 */
export const CONSUMPTION_PRESETS: Record<ConsumptionPreset, PresetConfig> = {
  day: {
    label: 'Dzień',
    description: 'Praca z domu, dzieci, gotowanie w ciągu dnia',
    icon: 'sun',
    hourlyWeights: [
      // 00-05: sleeping (low)
      0.2, 0.1, 0.1, 0.1, 0.1, 0.2,
      // 06-11: morning + work from home (rising)
      0.5, 0.8, 1.0, 1.2, 1.3, 1.2,
      // 12-17: midday + afternoon (high)
      1.3, 1.2, 1.1, 1.0, 1.1, 1.3,
      // 18-23: evening (declining)
      1.4, 1.2, 0.8, 0.5, 0.3, 0.2,
    ],
  },
  even: {
    label: 'Równomiernie',
    description: 'Standardowe zużycie, praca poza domem',
    icon: 'clock',
    hourlyWeights: [
      // 00-05: sleeping (low)
      0.3, 0.2, 0.2, 0.2, 0.2, 0.3,
      // 06-11: morning routine (moderate)
      0.7, 0.9, 0.6, 0.4, 0.4, 0.5,
      // 12-17: away/low usage (low-moderate)
      0.5, 0.5, 0.5, 0.5, 0.6, 0.8,
      // 18-23: evening peak (high)
      1.3, 1.4, 1.2, 0.9, 0.6, 0.4,
    ],
  },
  night: {
    label: 'Noc/wieczór',
    description: 'Ładowanie EV, pompa ciepła, nocna taryfa',
    icon: 'moon',
    hourlyWeights: [
      // 00-05: night operation (high — EV charging, heat pump)
      1.4, 1.3, 1.2, 1.1, 1.0, 0.8,
      // 06-11: morning (low-moderate)
      0.5, 0.4, 0.3, 0.2, 0.2, 0.2,
      // 12-17: daytime (low)
      0.2, 0.2, 0.2, 0.2, 0.3, 0.4,
      // 18-23: evening into night (rising)
      0.6, 0.8, 1.0, 1.2, 1.3, 1.4,
    ],
  },
};

/**
 * Normalize preset weights so they sum to 1.0
 */
export function getNormalizedWeights(preset: ConsumptionPreset): number[] {
  const weights = CONSUMPTION_PRESETS[preset].hourlyWeights;
  const sum = weights.reduce((a, b) => a + b, 0);
  return weights.map((w) => w / sum);
}
